import React, { useState, useEffect } from 'react';
import { TrendingUp, Briefcase, Users, Download, RefreshCw, CheckCircle, MapPin, DollarSign, Building2, Phone, Mail, Globe, Calendar, Clock, ChevronDown, ChevronUp, AlertTriangle, Target, ExternalLink, RotateCcw, Settings, Bot, Search, Zap } from 'lucide-react';
import {
  getAllADAJobs,
  getDailyADAJobStats,
  getADAJobAnalytics,
  triggerJobCrawl,
  getCrawlStatus,
  clearJobsCache
} from '../utils/adaJobRequirements';
import { crawlAllSources, saveJobsToGitHub, normalizeJob } from '../services/clientJobCrawler';

/**
 * Build CSV string from in-memory jobs array (avoids stale GitHub CDN fetch)
 */
const buildCSVFromJobs = (jobs) => {
  if (!jobs || jobs.length === 0) return '';

  const headers = [
    'Job ID', 'Job Title', 'Company', 'Location', 'Industry', 'Level',
    'Employment Type', 'Salary', 'Description', 'Skills',
    'ADA Requirements', 'Contact Email', 'Contact Phone', 'Company Website',
    'Application URL', 'Posted Date', 'Source'
  ];

  const esc = (val) => {
    if (val == null) return '';
    const str = Array.isArray(val) ? val.join('; ') : String(val);
    return `"${str.replace(/"/g, '""')}"`;
  };

  const rows = jobs.map(job => [
    job.id || '',
    job.jobTitle || job.title || '',
    job.company || '',
    job.location || '',
    job.industry || '',
    job.level || job.experience || '',
    job.employmentType || job.type || 'Full-time',
    job.salary || '',
    job.description || '',
    (job.skills || job.preferredSkills || []).join('; '),
    (job.adaRequirements || []).join('; '),
    job.contactEmail || '',
    job.contactPhone || '',
    job.companyWebsite || job.company_website || '',
    job.applicationUrl || job.apply_url || '',
    job.postedDate || job.posted_date || '',
    job.source || ''
  ].map(esc));

  return [
    headers.map(h => esc(h)).join(','),
    ...rows.map(r => r.join(','))
  ].join('\n');
};

const AdminADAJobMonitoring = () => {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('generate');
  const [expandedJob, setExpandedJob] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  
  // Crawler state
  const [crawling, setCrawling] = useState(false);
  const [crawlProgress, setCrawlProgress] = useState(null);
  const [crawledJobs, setCrawledJobs] = useState([]);
  
  // Crawl options (for real web scraping)
  const countryOptions = ['USA', 'India'];
  const [crawlCountries, setCrawlCountries] = useState(['USA']);
  const [crawlStartDate, setCrawlStartDate] = useState('');
  const [crawlEndDate, setCrawlEndDate] = useState('');
  const [additionalSkills, setAdditionalSkills] = useState('ADA compliance, WCAG 2.1, accessibility testing, screen reader testing');

  // Config state
  const [crawlConfig, setCrawlConfig] = useState({ jobsPerCrawl: 10, autoCrawlEnabled: true, crawlHour: 6, crawlMinute: 0, maxJobsInDB: 200 });
  const [customJobCount, setCustomJobCount] = useState('');
  const [dailySnapshots, setDailySnapshots] = useState([]);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      console.log('📊 Loading admin dashboard...');
      const allJobs = await getAllADAJobs();
      console.log('📋 Loaded jobs:', Array.isArray(allJobs) ? allJobs.length : 0);
      setJobs(Array.isArray(allJobs) ? allJobs.map(normalizeJob) : []);

      const dailyStats = await getDailyADAJobStats();
      setStats(dailyStats);

      const jobAnalytics = await getADAJobAnalytics();
      setAnalytics(jobAnalytics);
      
      // CLIENT-SIDE MODE: Config endpoints not available
      console.log('ℹ️ Running in client-side mode - config/snapshots not available');
    } catch (error) {
      console.error('❌ Error loading dashboard:', error);
      setErrorMessage('Failed to load dashboard data');
    }
  };

  const handleStartCrawl = async () => {
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      // CLIENT-SIDE MODE: Manual crawling not available
      const result = await triggerJobCrawl();
      
      if (result.success === false) {
        setErrorMessage('⚠️ Client-Side Mode: Automated job crawling is not available. Please update jobs manually in GitHub repository (Nibyor2025/newsletter/data/jobs.json) or use GitHub Actions for automated updates.');
      } else {
        setSuccessMessage('✅ Data refreshed from GitHub');
        setTimeout(() => {
          loadDashboard();
          setActiveTab('jobs');
        }, 2000);
      }
    } catch (error) {
      console.error('❌ Error:', error);
      setErrorMessage('⚠️ Client-Side Mode: Automated crawling not available. Update jobs via GitHub repository.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConfig = async (updates) => {
    // CLIENT-SIDE MODE: Config updates not available
    setErrorMessage('⚠️ Client-Side Mode: Configuration is managed via GitHub repository. Create a config.json file in your repository to manage settings.');
  };

  const handleResetDB = async () => {
    if (!window.confirm('This will clear the local cache. Jobs will reload from GitHub on next fetch.')) {
      return;
    }
    // CLIENT-SIDE MODE: Just clear local cache
    setJobs([]);
    setSuccessMessage('✅ Local cache cleared. Jobs will reload from GitHub on next refresh.');
    // Clear cache by triggering reload
    setTimeout(() => loadDashboard(), 1000);
  };

  const handleClientSideCrawl = async () => {
    setCrawling(true);
    setErrorMessage('');
    setSuccessMessage('');
    setCrawlProgress({ status: 'starting', message: 'Initializing client-side job crawler...' });
    setCrawledJobs([]);
    
    const options = {
      country: crawlCountries,
      startDate: crawlStartDate,
      endDate: crawlEndDate,
      skills: additionalSkills.split(',').map(s => s.trim()).filter(Boolean)
    };

    console.log(`🚀 Starting crawl with options:`, {
      countries: crawlCountries,
      startDate: crawlStartDate,
      endDate: crawlEndDate,
      skills: options.skills
    });
    
    console.log(`🔍 Verifying crawl state - crawlCountries array:`, crawlCountries);
    console.log(`🔍 Is crawlCountries an array?`, Array.isArray(crawlCountries));
    console.log(`🔍 crawlCountries length:`, crawlCountries.length);

    try {
      // Crawl all sources with progress updates and options
      const newJobs = await crawlAllSources((progress) => {
        setCrawlProgress({
          status: progress.status,
          current: progress.current,
          total: progress.total,
          source: progress.source,
          jobsFound: progress.jobsFound,
          error: progress.error
        });
      }, options);
      
      // Only use REAL crawled jobs — no synthetic/fabricated data
      const finalJobs = newJobs;

      if (finalJobs.length === 0) {
        setCrawlProgress({
          status: 'completed',
          message: '⚠️ No real jobs found matching your criteria. Try different keywords, broader date range, or different countries.'
        });
        setCrawling(false);
        return;
      }

      // Show crawled jobs preview immediately
      setCrawledJobs(finalJobs);

      setCrawlProgress({ 
        status: 'saving', 
        message: `Saving ${finalJobs.length} jobs to GitHub...` 
      });
      
      // Merge with existing jobs (avoid duplicates by ID)
      const existingIds = new Set(jobs.map(j => j.id));
      const uniqueNewJobs = finalJobs.filter(j => !existingIds.has(j.id));
      const mergedJobs = [...jobs, ...uniqueNewJobs].map(normalizeJob);
      
      console.log(`📊 Final jobs before save:`, {
        crawledCount: finalJobs.length,
        uniqueNewCount: uniqueNewJobs.length,
        existingCount: existingIds.size,
        totalAfterMerge: mergedJobs.length,
        jobsByCountry: mergedJobs.reduce((acc, job) => {
          const loc = job.location || 'Unknown';
          acc[loc] = (acc[loc] || 0) + 1;
          return acc;
        }, {})
      });
      
      // Save to GitHub
      await saveJobsToGitHub(mergedJobs);
      
      setCrawlProgress({ 
        status: 'completed', 
        message: `✅ Successfully saved ${uniqueNewJobs.length} new jobs to GitHub! (${finalJobs.length - uniqueNewJobs.length} duplicates skipped)` 
      });
      
      setSuccessMessage(`✅ Crawl completed! Added ${uniqueNewJobs.length} new jobs (${mergedJobs.length} total) to GitHub repository.`);
      
      // Update local state IMMEDIATELY (don't wait for GitHub CDN to update)
      setJobs(mergedJobs);
      
      // Clear client cache so next explicit refresh fetches fresh data
      try {
        clearJobsCache();
      } catch (e) {
        console.warn('Could not clear jobs cache:', e);
      }

      // Update stats/analytics from the local data
      const todayStr = new Date().toISOString().split('T')[0];
      setStats({
        totalJobs: mergedJobs.length,
        todayJobs: mergedJobs.filter(j => (j.postedDate || j.posted_date || '').startsWith(todayStr)).length
      });

      setTimeout(() => {
        setCrawlProgress(null);
      }, 3000);
      
    } catch (error) {
      console.error('❌ Client-side crawl failed:', error);
      setCrawlProgress({ 
        status: 'failed', 
        message: `❌ Crawl failed: ${error.message}` 
      });
      setErrorMessage(`Crawl failed: ${error.message}. Check your network connection and try again.`);
      
      // No synthetic fallback — only real data
      // No synthetic fallback — only real data
      try {
        // Just log the error, don't generate fake jobs
        console.warn('Crawl failed — no synthetic fallback. Only real data is shown.');
      } catch (fallbackError) {
        console.error('❌ Fallback also failed:', fallbackError);
      }
    } finally {
      setCrawling(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      // Export from current in-memory jobs state (not from GitHub which may be stale)
      const csv = buildCSVFromJobs(jobs);
      if (!csv) { setErrorMessage('No jobs to export'); return; }
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ada-jobs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setSuccessMessage('✅ CSV exported successfully!');
    } catch (error) {
      setErrorMessage('Failed to export CSV');
    }
  };

  const handleCheckCrawlStatus = async () => {
    try {
      const status = await getCrawlStatus();
      if (status) {
        setSuccessMessage(`📊 Total jobs: ${status.totalJobsInDatabase} • Last updated: ${status.lastUpdated ? new Date(status.lastUpdated).toLocaleString() : 'Never'} • Status: ${status.crawlStatus}`);
      }
    } catch (error) {
      setErrorMessage('Failed to check crawl status');
    }
  };

  // Filtered jobs
  const filteredJobs = jobs.filter(job => {
    const matchSearch = !searchTerm ||
      (job.jobTitle || job.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job.company || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job.location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchIndustry = !industryFilter || job.industry === industryFilter;
    const matchLevel = !levelFilter || (job.level || job.experience || '') === levelFilter;
    return matchSearch && matchIndustry && matchLevel;
  });

  const industries = [...new Set(jobs.map(j => j.industry).filter(Boolean))];
  const levels = [...new Set(jobs.map(j => j.level || j.experience).filter(Boolean))];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Briefcase className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">ADA Job Intelligence Dashboard</h1>
          </div>
          <p className="text-gray-600">AI-powered job crawling for business analyst review — Full details including company, location, contacts, descriptions</p>
        </div>

        {/* Client-Side Mode Info Banner */}
        <div className="mb-6 p-4 bg-blue-50 text-blue-900 rounded-lg border-l-4 border-blue-500">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">i</div>
            <div className="flex-1">
              <div className="font-semibold mb-1">ℹ️ Client-Side Mode Active</div>
              <div className="text-sm text-blue-800">
                <p className="mb-2">This admin panel is running in client-side mode with GitHub as the data source.</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li><strong>Jobs Data:</strong> Fetched from GitHub repository (Nibyor2025/newsletter/data/jobs.json)</li>
                  <li><strong>Automated Crawling:</strong> Not available - Update jobs manually or use GitHub Actions</li>
                  <li><strong>Configuration:</strong> Managed via GitHub repository files</li>
                  <li><strong>Real-time Updates:</strong> Reflected after GitHub repository changes</li>
                </ul>
                <p className="mt-2 text-xs">
                  <a href="https://github.com/Nibyor2025/newsletter" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline font-medium">→ Manage data in GitHub Repository</a>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Status Messages */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 text-green-800 rounded-lg border border-green-200 flex items-start gap-2">
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span className="whitespace-pre-line">{successMessage}</span>
            <button onClick={() => setSuccessMessage('')} className="ml-auto text-green-600 hover:text-green-800">✕</button>
          </div>
        )}
        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 text-red-800 rounded-lg border border-red-200">
            <div className="font-bold mb-1">❌ Error</div>
            <div className="text-sm">{errorMessage}</div>
            <button onClick={() => setErrorMessage('')} className="mt-2 text-red-600 hover:text-red-800 text-sm underline">Dismiss</button>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard icon={<Briefcase className="w-6 h-6" />} title="Total Jobs" value={stats.totalJobs} subtext={`${stats.todayJobs || 0} added today`} color="blue" />
            <StatCard icon={<Building2 className="w-6 h-6" />} title="Companies" value={[...new Set(jobs.map(j => j.company))].length} subtext="Unique companies" color="purple" />
            <StatCard icon={<MapPin className="w-6 h-6" />} title="Locations" value={[...new Set(jobs.map(j => j.location))].length} subtext="US cities" color="green" />
            <StatCard icon={<TrendingUp className="w-6 h-6" />} title="Industries" value={industries.length} subtext="Sectors covered" color="orange" />
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 flex gap-1 border-b-2 border-gray-200 overflow-x-auto">
          {[
            { key: 'generate', label: '� Data Management' },
            { key: 'jobs', label: `📋 All Jobs (${jobs.length})` },
            { key: 'overview', label: '📊 Overview' },
            { key: 'analytics', label: '📈 Analytics' },
            { key: 'config', label: '⚙️ Settings' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-3 font-medium whitespace-nowrap transition ${
                activeTab === tab.key
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-lg">

          {/* ===== DATA MANAGEMENT TAB (CLIENT-SIDE MODE) ===== */}
          {activeTab === 'generate' && (
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6">📊 Data Management (Client-Side Mode)</h2>

              {/* Crawl Options */}
              <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Country</label>
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    {countryOptions.map((ctry) => (
                      <label key={ctry} className="inline-flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={crawlCountries.includes(ctry)}
                          onChange={() => {
                            setCrawlCountries(prev => {
                              if (prev.includes(ctry)) {
                                return prev.filter(c => c !== ctry);
                              } else {
                                return [...prev, ctry];
                              }
                            });
                          }}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                        <span className="text-sm">{ctry === 'USA' ? 'United States' : ctry}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    value={crawlStartDate}
                    onChange={(e) => setCrawlStartDate(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <input
                    type="date"
                    value={crawlEndDate}
                    onChange={(e) => setCrawlEndDate(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="text-sm text-gray-500">
                  <p>Specify a date range and country for the crawl. Leave blank for defaults.</p>
                </div>
              </div>
              {/* Skills input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700">Skill Tags</label>
                <input
                  type="text"
                  value={additionalSkills}
                  onChange={(e) => setAdditionalSkills(e.target.value)}
                  placeholder="Comma-separated keywords (e.g. ADA compliance, WCAG)"
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">These tags will be appended to each crawled job's skills list. Modify to emphasize particular ADA topics.</p>
              </div>

              {/* Quick Actions */}
              <div className="mb-8 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <button
                    onClick={() => {
                      setLoading(true);
                      setErrorMessage('');
                      setSuccessMessage('');
                      loadDashboard().then(() => {
                        setSuccessMessage('✅ Data refreshed successfully from GitHub repository!');
                        setLoading(false);
                      }).catch((err) => {
                        setErrorMessage(`Failed to refresh: ${err.message}`);
                        setLoading(false);
                      });
                    }}
                    disabled={loading || crawling}
                    className={`px-6 py-4 font-semibold rounded-lg flex items-center justify-center gap-3 transition text-lg ${
                      loading || crawling
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl'
                    }`}
                  >
                    <RefreshCw className={`w-6 h-6 ${loading ? 'animate-spin' : ''}`} />
                    {loading ? 'Refreshing...' : 'Refresh from GitHub'}
                  </button>

                  <button
                    onClick={handleClientSideCrawl}
                    disabled={loading || crawling}
                    className={`px-6 py-4 font-semibold rounded-lg flex items-center justify-center gap-3 transition text-lg ${
                      crawling
                        ? 'bg-yellow-500 text-white cursor-wait'
                        : loading
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl'
                    }`}
                  >
                    <Search className={`w-6 h-6 ${crawling ? 'animate-pulse' : ''}`} />
                    {crawling ? 'Crawling...' : '🤖 Crawl Jobs Now'}
                  </button>

                  <button
                    onClick={() => window.open('https://github.com/Nibyor2025/newsletter/blob/main/data/jobs.json', '_blank')}
                    className="px-6 py-4 font-semibold rounded-lg flex items-center justify-center gap-3 bg-gray-800 text-white hover:bg-gray-900 shadow-lg hover:shadow-xl transition text-lg"
                  >
                    <Globe className="w-6 h-6" />
                    Open GitHub Repository
                  </button>
                </div>

                {/* Crawler Progress */}
                {crawlProgress && (
                  <div className={`mb-6 p-4 rounded-lg border-2 ${
                    crawlProgress.status === 'completed' ? 'bg-green-50 border-green-300' :
                    crawlProgress.status === 'failed' ? 'bg-red-50 border-red-300' :
                    'bg-yellow-50 border-yellow-300'
                  }`}>
                    <div className="flex items-center gap-3 mb-2">
                      {crawlProgress.status === 'completed' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : crawlProgress.status === 'failed' ? (
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                      ) : (
                        <RefreshCw className="w-5 h-5 text-yellow-600 animate-spin" />
                      )}
                      <div className="flex-1">
                        <div className="font-semibold text-sm">
                          {crawlProgress.status === 'crawling' && crawlProgress.source && (
                            <span>Crawling {crawlProgress.source} ({crawlProgress.current}/{crawlProgress.total})...</span>
                          )}
                          {crawlProgress.message}
                        </div>
                        {crawlProgress.jobsFound !== undefined && (
                          <div className="text-xs text-gray-600 mt-1">Found {crawlProgress.jobsFound} jobs</div>
                        )}
                      </div>
                    </div>
                    {crawlProgress.current && crawlProgress.total && (
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div 
                          className="bg-blue-600 h-2 transition-all duration-300"
                          style={{ width: `${(crawlProgress.current / crawlProgress.total) * 100}%` }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Crawled Jobs Preview */}
                {crawledJobs.length > 0 && (
                  <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3">
                      🎯 Crawled Jobs Preview ({crawledJobs.length})
                    </h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {crawledJobs.slice(0, 10).map((job, idx) => (
                        <div key={idx} className="text-sm p-2 bg-gray-50 rounded border border-gray-200">
                          <div className="font-semibold text-gray-900">{job.title}</div>
                          <div className="text-gray-600 text-xs">{job.company} • {job.location} • {job.source}</div>
                        </div>
                      ))}
                      {crawledJobs.length > 10 && (
                        <div className="text-center text-gray-500 text-xs">
                          ... and {crawledJobs.length - 10} more
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <button 
                    onClick={handleExportCSV}
                    className="flex-1 px-4 py-3 font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition flex items-center justify-center gap-2">
                    <Download className="w-5 h-5" /> Export Current Data (CSV)
                  </button>
                  <button 
                    onClick={handleResetDB}
                    disabled={loading}
                    className="flex-1 px-4 py-3 font-medium rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition flex items-center justify-center gap-2">
                    <RotateCcw className="w-5 h-5" /> Clear Cache
                  </button>
                </div>
              </div>

              {/* How to Update Jobs */}
              <div className="mb-8 p-6 bg-white rounded-xl border-2 border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-blue-600" />
                  How to Update Jobs Data
                </h3>
                
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Edit GitHub Repository</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        Navigate to <code className="px-2 py-1 bg-gray-100 rounded text-xs">Nibyor2025/newsletter/data/jobs.json</code> and edit the file directly on GitHub.
                      </p>
                      <a 
                        href="https://github.com/Nibyor2025/newsletter/edit/main/data/jobs.json" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Edit jobs.json on GitHub
                      </a>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Use GitHub Actions (Automated)</h4>
                      <p className="text-sm text-gray-600 mb-2">
                        Set up a GitHub Actions workflow to automatically fetch and update job data on a schedule (daily, weekly, etc.)
                      </p>
                      <a 
                        href="https://github.com/Nibyor2025/newsletter/actions" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        <Bot className="w-4 h-4" />
                        View GitHub Actions
                      </a>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Upload JSON File</h4>
                      <p className="text-sm text-gray-600">
                        You can also upload a <code className="px-2 py-1 bg-gray-100 rounded text-xs">jobs.json</code> file directly to the repository through the GitHub web interface.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Current Data Info */}
              <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <h4 className="font-semibold text-green-900 flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5" /> Current Data Status
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-green-700 font-medium">Total Jobs</div>
                    <div className="text-2xl font-bold text-green-900">{jobs.length}</div>
                  </div>
                  <div>
                    <div className="text-green-700 font-medium">Companies</div>
                    <div className="text-2xl font-bold text-green-900">{[...new Set(jobs.map(j => j.company))].length}</div>
                  </div>
                  <div>
                    <div className="text-green-700 font-medium">Industries</div>
                    <div className="text-2xl font-bold text-green-900">{industries.length}</div>
                  </div>
                  <div>
                    <div className="text-green-700 font-medium">Data Source</div>
                    <div className="text-sm font-semibold text-green-900 mt-1">GitHub + Cache</div>
                  </div>
                </div>
                <p className="text-xs text-green-700 mt-4">
                  💡 Click "Refresh from GitHub" above to fetch the latest data from the repository
                </p>
              </div>
            </div>
          )}

          {/* ===== JOBS TAB ===== */}
          {activeTab === 'jobs' && (
            <div className="p-6">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <h2 className="text-2xl font-bold mr-auto">Crawled Jobs ({filteredJobs.length})</h2>
                <button onClick={handleExportCSV} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium">
                  <Download className="w-4 h-4" /> Export CSV
                </button>
                <button onClick={loadDashboard} className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium">
                  <RefreshCw className="w-4 h-4" /> Refresh
                </button>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="md:col-span-2">
                  <input
                    type="text"
                    placeholder="Search jobs, companies, locations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select value={industryFilter} onChange={(e) => setIndustryFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                  <option value="">All Industries</option>
                  {industries.map(ind => <option key={ind} value={ind}>{ind}</option>)}
                </select>
                <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                  <option value="">All Levels</option>
                  {levels.map(lvl => <option key={lvl} value={lvl}>{lvl.charAt(0).toUpperCase() + lvl.slice(1)}</option>)}
                </select>
              </div>

              {/* Job Cards */}
              <div className="space-y-4">
                {filteredJobs.length === 0 ? (
                  <div className="p-12 text-center bg-blue-50 rounded-lg border-2 border-dashed border-blue-200">
                    <Briefcase className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-3 text-lg">No jobs yet. Start a crawl to generate realistic ADA job postings.</p>
                    <button onClick={() => setActiveTab('generate')} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                      Go to Crawl Tab
                    </button>
                  </div>
                ) : (
                  filteredJobs.map(job => (
                    <JobCardDetailed
                      key={job.id}
                      job={job}
                      isExpanded={expandedJob === job.id}
                      onToggle={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {/* ===== OVERVIEW TAB ===== */}
          {activeTab === 'overview' && (
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6">Dashboard Overview</h2>
              {analytics ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="p-6 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-4">Jobs by Industry</h3>
                    <div className="space-y-2">
                      {Object.entries(analytics.byIndustry || {}).map(([industry, data]) => (
                        <div key={industry} className="flex justify-between items-center">
                          <span className="text-gray-700">{industry}</span>
                          <span className="font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded">{typeof data === 'object' ? data.count : data} jobs</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="p-6 bg-purple-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-4">Jobs by Level</h3>
                    <div className="space-y-2">
                      {Object.entries(analytics.byLevel || {}).map(([level, count]) => (
                        <div key={level} className="flex justify-between items-center">
                          <span className="text-gray-700 capitalize">{level}</span>
                          <span className="font-bold text-purple-600 bg-purple-100 px-2 py-0.5 rounded">{count} jobs</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="p-6 bg-green-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-4">Top Companies</h3>
                    <div className="space-y-2">
                      {[...new Set(jobs.map(j => j.company))].slice(0, 10).map(company => (
                        <div key={company} className="flex justify-between items-center">
                          <span className="text-gray-700 text-sm">{company}</span>
                          <span className="font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded text-sm">{jobs.filter(j => j.company === company).length}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="p-6 bg-orange-50 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-4">Locations</h3>
                    <div className="space-y-2">
                      {[...new Set(jobs.map(j => j.location))].slice(0, 10).map(loc => (
                        <div key={loc} className="flex justify-between items-center">
                          <span className="text-gray-700 text-sm">{loc}</span>
                          <span className="font-bold text-orange-600 bg-orange-100 px-2 py-0.5 rounded text-sm">{jobs.filter(j => j.location === loc).length}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">No analytics data available. Run a crawl first.</p>
              )}
            </div>
          )}

          {/* ===== ANALYTICS TAB ===== */}
          {activeTab === 'analytics' && (
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6">Business Intelligence</h2>
              {jobs.length > 0 ? (
                <div className="space-y-6">
                  <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                    <h3 className="font-semibold text-gray-900 mb-3">Summary for Business Analysts</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div><p className="text-3xl font-bold text-blue-600">{jobs.length}</p><p className="text-sm text-gray-600">Total Postings</p></div>
                      <div><p className="text-3xl font-bold text-purple-600">{[...new Set(jobs.map(j => j.company))].length}</p><p className="text-sm text-gray-600">Companies</p></div>
                      <div><p className="text-3xl font-bold text-green-600">{industries.length}</p><p className="text-sm text-gray-600">Industries</p></div>
                      <div><p className="text-3xl font-bold text-orange-600">{jobs.filter(j => j.complianceGaps && j.complianceGaps.length > 0).length}</p><p className="text-sm text-gray-600">With Compliance Gaps</p></div>
                    </div>
                  </div>
                  <div className="p-6 bg-red-50 rounded-lg border border-red-200">
                    <h3 className="font-semibold text-red-900 mb-3 flex items-center gap-2"><Target className="w-5 h-5" /> Top Business Opportunities</h3>
                    <div className="space-y-3">
                      {jobs.filter(j => j.businessOpportunity).slice(0, 5).map(job => (
                        <div key={job.id} className="p-3 bg-white rounded border border-red-100">
                          <p className="font-medium text-gray-900">{job.company} — {job.jobTitle || job.title}</p>
                          <p className="text-sm text-gray-700 mt-1">{job.businessOpportunity}</p>
                          {job.contactEmail && <p className="text-xs text-blue-600 mt-1">📧 {job.contactEmail} • 📞 {job.contactPhone}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">No data yet. Start a crawl to see analytics.</p>
              )}
            </div>
          )}

          {/* ===== CONFIG/SETTINGS TAB ===== */}
          {activeTab === 'config' && (
            <div className="p-8">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2"><Settings className="w-6 h-6 text-gray-600" /> Crawl Configuration</h2>

              {/* Current Config */}
              <div className="space-y-6">
                <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                  <h3 className="font-semibold text-gray-900 mb-4">Job Crawl Settings</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Jobs Per Crawl */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Jobs Per Crawl (Default)</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="1"
                          max="50"
                          value={crawlConfig.jobsPerCrawl}
                          onChange={(e) => setCrawlConfig(prev => ({ ...prev, jobsPerCrawl: Number(e.target.value) }))}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={() => handleUpdateConfig({ jobsPerCrawl: crawlConfig.jobsPerCrawl })}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                        >
                          Save
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Number of jobs generated per crawl (1-50). Used for daily auto-crawl and default manual crawl.</p>
                    </div>

                    {/* Max Jobs in DB */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Jobs in Database</label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="10"
                          max="1000"
                          value={crawlConfig.maxJobsInDB}
                          onChange={(e) => setCrawlConfig(prev => ({ ...prev, maxJobsInDB: Number(e.target.value) }))}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={() => handleUpdateConfig({ maxJobsInDB: crawlConfig.maxJobsInDB })}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                        >
                          Save
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Database trims to this limit. Old jobs are removed first.</p>
                    </div>

                    {/* Auto Crawl Toggle */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Daily Auto-Crawl</label>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleUpdateConfig({ autoCrawlEnabled: !crawlConfig.autoCrawlEnabled })}
                          className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
                            crawlConfig.autoCrawlEnabled
                              ? 'bg-green-600 text-white hover:bg-green-700'
                              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                          }`}
                        >
                          {crawlConfig.autoCrawlEnabled ? '✅ Enabled' : '⏸ Disabled'}
                        </button>
                        <span className="text-xs text-gray-500">
                          Auto-crawls run daily at {String(crawlConfig.crawlHour).padStart(2, '0')}:{String(crawlConfig.crawlMinute).padStart(2, '0')}
                        </span>
                      </div>
                    </div>

                    {/* Crawl Time */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Crawl Time (24h)</label>
                      <div className="flex gap-2 items-center">
                        <input
                          type="number"
                          min="0"
                          max="23"
                          value={crawlConfig.crawlHour}
                          onChange={(e) => setCrawlConfig(prev => ({ ...prev, crawlHour: Number(e.target.value) }))}
                          className="w-16 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
                        />
                        <span className="text-gray-500">:</span>
                        <input
                          type="number"
                          min="0"
                          max="59"
                          value={crawlConfig.crawlMinute}
                          onChange={(e) => setCrawlConfig(prev => ({ ...prev, crawlMinute: Number(e.target.value) }))}
                          className="w-16 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
                        />
                        <button
                          onClick={() => handleUpdateConfig({ crawlHour: crawlConfig.crawlHour, crawlMinute: crawlConfig.crawlMinute })}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gemini API Info */}
                <div className="p-6 bg-amber-50 rounded-xl border border-amber-200">
                  <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2"><Bot className="w-5 h-5" /> Gemini AI Configuration</h3>
                  <div className="text-sm text-amber-800 space-y-1">
                    <p><strong>API Key:</strong> AIzaSyDl5yBY...pnsPf0 (configured in server)</p>
                    <p><strong>Project:</strong> projects/390955946567</p>
                    <p><strong>Models (fallback chain):</strong> gemini-2.0-flash → gemini-2.0-flash-lite → gemini-1.5-flash</p>
                    <p><strong>Fallback:</strong> High-quality 10-job dataset when all models are rate-limited</p>
                  </div>
                </div>

                {/* Daily Snapshots Archive */}
                <div className="p-6 bg-green-50 rounded-xl border border-green-200">
                  <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5" /> Daily Snapshots (Git Archive)
                  </h3>
                  <p className="text-sm text-green-800 mb-3">
                    Jobs are saved daily as JSON files at <code className="bg-green-100 px-1 py-0.5 rounded">data/jobs-daily/jobs-YYYY-MM-DD.json</code> for git tracking.
                  </p>
                  {dailySnapshots.length > 0 ? (
                    <div className="max-h-60 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-gray-500 uppercase border-b border-green-200">
                            <th className="pb-2 pr-4">Date</th>
                            <th className="pb-2 pr-4">Jobs</th>
                            <th className="pb-2 pr-4">Industries</th>
                            <th className="pb-2">Generated</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-green-100">
                          {dailySnapshots.map(snap => (
                            <tr key={snap.filename} className="text-gray-700">
                              <td className="py-2 pr-4 font-medium">{snap.date}</td>
                              <td className="py-2 pr-4">{snap.totalJobs}</td>
                              <td className="py-2 pr-4">{(snap.industries || []).length}</td>
                              <td className="py-2 text-xs text-gray-500">{snap.generatedAt ? new Date(snap.generatedAt).toLocaleString() : 'N/A'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No snapshots found yet. Snapshots are created on first crawl each day.</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== Detailed Job Card Component =====
const JobCardDetailed = ({ job, isExpanded, onToggle }) => {
  const levelColors = {
    entry: 'bg-green-100 text-green-800',
    mid: 'bg-blue-100 text-blue-800',
    'mid-level': 'bg-blue-100 text-blue-800',
    senior: 'bg-purple-100 text-purple-800',
    lead: 'bg-orange-100 text-orange-800',
    director: 'bg-red-100 text-red-800'
  };

  // Use normalized aliases so both crawler and consumer field names work
  const jobTitle = job.jobTitle || job.title || '';
  const jobLevel = job.level || job.experience || '';
  const jobEmploymentType = job.employmentType || job.type || 'Full-time';
  const jobPostedDate = job.postedDate || job.posted_date || '';
  const jobPreferredSkills = job.preferredSkills || job.skills || [];
  const jobCompanyWebsite = job.companyWebsite || job.company_website || '';
  const jobApplicationUrl = job.applicationUrl || job.apply_url || '';

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition bg-white">
      {/* Job Header - Always visible */}
      <div className="p-5 cursor-pointer" onClick={onToggle}>
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900">{jobTitle}</h3>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-gray-600">
              <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" /> {job.company}</span>
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {job.location || 'Not specified'}</span>
              <span className="flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> {job.salary || 'Not disclosed'}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${levelColors[jobLevel.toLowerCase()] || 'bg-gray-100 text-gray-800'}`}>
                {jobLevel.charAt(0).toUpperCase() + jobLevel.slice(1)} Level
              </span>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{job.industry}</span>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">{jobEmploymentType}</span>
              {job.remotePolicy && <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">{job.remotePolicy}</span>}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right text-xs text-gray-500">
              <div className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Posted: {formatDate(jobPostedDate)}</div>
              {job.applicationDeadline && <div className="flex items-center gap-1 mt-1"><Clock className="w-3 h-3" /> Deadline: {job.applicationDeadline}</div>}
            </div>
            {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </div>

        {/* Brief description always visible */}
        <p className="text-sm text-gray-600 mt-3 line-clamp-2">{job.description}</p>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-gray-100 p-5 bg-gray-50/50 space-y-5">
          {/* Company & Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Building2 className="w-4 h-4 text-blue-600" /> Company Details</h4>
              <div className="space-y-1.5 text-sm">
                <p><span className="text-gray-500">Company:</span> <span className="font-medium">{job.company}</span></p>
                {job.companySize && <p><span className="text-gray-500">Size:</span> {job.companySize} employees</p>}
                {job.department && <p><span className="text-gray-500">Department:</span> {job.department}</p>}
                {jobCompanyWebsite && (
                  <p className="flex items-center gap-1">
                    <Globe className="w-3.5 h-3.5 text-gray-400" />
                    <a href={jobCompanyWebsite} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{jobCompanyWebsite}</a>
                  </p>
                )}
              </div>
            </div>
            <div className="p-4 bg-white rounded-lg border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Phone className="w-4 h-4 text-green-600" /> Contact Information</h4>
              <div className="space-y-1.5 text-sm">
                {job.contactPerson && <p><span className="text-gray-500">Contact:</span> <span className="font-medium">{job.contactPerson}</span></p>}
                {job.contactEmail && (
                  <p className="flex items-center gap-1">
                    <Mail className="w-3.5 h-3.5 text-gray-400" />
                    <a href={`mailto:${job.contactEmail}`} className="text-blue-600 hover:underline">{job.contactEmail}</a>
                  </p>
                )}
                {job.contactPhone && (
                  <p className="flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5 text-gray-400" />
                    <a href={`tel:${job.contactPhone}`} className="text-blue-600 hover:underline">{job.contactPhone}</a>
                  </p>
                )}
                {jobApplicationUrl && (
                  <p className="flex items-center gap-1 mt-2">
                    <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                    <a href={jobApplicationUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">Apply Here</a>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Full Description */}
          <div className="p-4 bg-white rounded-lg border border-gray-200">
            <h4 className="font-semibold text-gray-900 mb-2">Job Description</h4>
            <p className="text-sm text-gray-700 leading-relaxed">{job.description}</p>
          </div>

          {/* Responsibilities & Qualifications */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {job.responsibilities && job.responsibilities.length > 0 && (
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Key Responsibilities</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {job.responsibilities.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
            {job.qualifications && job.qualifications.length > 0 && (
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Qualifications</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {job.qualifications.map((q, i) => <li key={i}>{q}</li>)}
                </ul>
              </div>
            )}
          </div>

          {/* Skills & Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {jobPreferredSkills && jobPreferredSkills.length > 0 && (
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Preferred Skills</h4>
                <div className="flex flex-wrap gap-2">
                  {jobPreferredSkills.map((s, i) => (
                    <span key={i} className="px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">{s}</span>
                  ))}
                </div>
              </div>
            )}
            {job.benefits && job.benefits.length > 0 && (
              <div className="p-4 bg-white rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">Benefits</h4>
                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                  {job.benefits.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            )}
          </div>

          {/* ADA Business Intelligence */}
          <div className="p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-200">
            <h4 className="font-semibold text-red-900 mb-3 flex items-center gap-2">
              <Target className="w-4 h-4" /> ADA Business Intelligence
            </h4>
            <div className="space-y-3">
              {job.adaRelevance && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">ADA Relevance</p>
                  <p className="text-sm text-gray-700">{job.adaRelevance}</p>
                </div>
              )}
              {job.complianceGaps && job.complianceGaps.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">Compliance Gaps Identified</p>
                  <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                    {job.complianceGaps.map((g, i) => <li key={i}>{g}</li>)}
                  </ul>
                </div>
              )}
              {job.businessOpportunity && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">Business Opportunity</p>
                  <p className="text-sm text-green-800 font-medium">{job.businessOpportunity}</p>
                </div>
              )}
              {/* Legacy adaInfo support */}
              {job.adaInfo && (
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-red-200">
                  <div>
                    <p className="text-xs text-gray-500">ADA Opportunity Score</p>
                    <p className="text-lg font-bold text-blue-600">{job.adaInfo.adaComplianceOpportunity}/10</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Remote Suitability</p>
                    <p className="text-lg font-bold text-green-600">{job.adaInfo.remoteWorkSuitability}/10</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper: format date
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return dateStr; }
};

// Helper: Stat Card
const StatCard = ({ icon, title, value, subtext, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    purple: 'bg-purple-100 text-purple-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600'
  };
  return (
    <div className="bg-white rounded-lg shadow p-5">
      <div className={`w-10 h-10 rounded-lg ${colorClasses[color]} flex items-center justify-center mb-3`}>{icon}</div>
      <h3 className="text-gray-500 text-sm font-medium">{title}</h3>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      <p className="text-xs text-gray-400 mt-1">{subtext}</p>
    </div>
  );
};

export default AdminADAJobMonitoring;
